"use client"

import { useId, useMemo, useState } from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"


export type VoiceLanguageOption = {
  value: string
  label: string
}

type VoiceLanguageSelectorProps = {
  options: readonly VoiceLanguageOption[]
  value: string[]
  onChange: (languages: string[]) => void
  disabled?: boolean
  loading?: boolean
  error?: string | null
}

export function VoiceLanguageSelector({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  error,
}: VoiceLanguageSelectorProps) {
  const tUi = useTranslations("Voice.AgentsVoiceVoiceLanguageSelector")

  const [open, setOpen] = useState(false)
  const triggerId = useId()
  const optionLabels = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options],
  )
  const selectionSummary = getSelectionSummary(tUi, value, optionLabels)
  const placeholder = getPlaceholder(tUi, { loading, error: Boolean(error) })

  const toggleLanguage = (language: string) => {
    if (!value.includes(language)) {
      onChange([...value, language])
      return
    }

    onChange(value.filter((selected) => selected !== language))
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={triggerId}><span key={tUi("languages")}>{tUi("languages")}</span></Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={triggerId}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={tUi("languages2")}
            disabled={disabled || options.length === 0}
            className="w-full justify-between font-normal"
          >
            <span
              className={cn(
                "truncate text-left",
                value.length === 0 && "text-muted-foreground",
              )}
            >
              {selectionSummary || placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command>
            <CommandInput placeholder={tUi("searchLanguages")} />
            <CommandList aria-multiselectable="true">
              <CommandEmpty><span key={tUi("noLanguagesFound")}>{tUi("noLanguagesFound")}</span></CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const selectedIndex = value.indexOf(option.value)
                  const isSelected = selectedIndex >= 0
                  const isPrimary = selectedIndex === 0
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value}`}
                      onSelect={() => toggleLanguage(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span>{option.label}</span>
                      {isPrimary ? (
                        <span className="ml-auto text-xs text-muted-foreground">
                          <span key={tUi("primary")}>{tUi("primary")}</span></span>
                      ) : null}
                      <span key={isSelected ? tUi("selected") : tUi("notSelected")} className="sr-only">
                        {isSelected ? tUi("selected") : tUi("notSelected")}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-xs text-muted-foreground">
        <span key={tUi("theFirstSelectedLanguageIsPrimaryTheAgent")}>{tUi("theFirstSelectedLanguageIsPrimaryTheAgent")}</span></p>
    </div>
  )
}

function getSelectionSummary(tUi: ReturnType<typeof useTranslations<"Voice.AgentsVoiceVoiceLanguageSelector">>,
  languages: string[],
  optionLabels: Map<string, string>,
): string {
  return languages
    .map((language, index) => {
      const label = optionLabels.get(language) ?? language.toUpperCase()
      return index === 0 ? tUi("value0Primary", { value0: String(label) }) : label
    })
    .join(", ")
}

function getPlaceholder(tUi: ReturnType<typeof useTranslations<"Voice.AgentsVoiceVoiceLanguageSelector">>, {
  loading,
  error,
}: {
  loading: boolean
  error: boolean
}): string {
  if (loading) return tUi("loadingLanguages")
  if (error) return tUi("unableToLoadLanguages")
  return tUi("selectLanguages")
}
